import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import LinksEnum from "../../components/enums/LinksEnum";
import PageEnum from "../../components/enums/PageEnum";

// Icons
import { ChevronDownIcon, ChevronRightIcon, BookOpenIcon, LightBulbIcon, AcademicCapIcon, ShieldExclamationIcon, CodeBracketIcon, WrenchScrewdriverIcon, DocumentTextIcon, BeakerIcon, PuzzlePieceIcon, LockClosedIcon } from '@heroicons/react/24/outline';

interface DocumentationNavProps {
    page: string;
    currentSection?: string;
}

interface NavItemProps {
    title: string;
    url: string;
    icon?: React.ReactNode;
    isActive?: boolean;
    isOpen?: boolean;
    onClick?: () => void;
    children?: React.ReactNode;
    badge?: string | number;
    isSubItem?: boolean;
    loginRequired?: boolean;
}

interface SubNavItemProps {
    title: string;
    url: string;
    isActive?: boolean;
    innerNav?: Array<{ title: string; url: string; }>;
}

// Navigation components for modern UI
const NavItem: React.FC<NavItemProps> = ({ 
    title, 
    url, 
    icon, 
    isActive = false, 
    isOpen = false, 
    onClick, 
    children, 
    badge,
    isSubItem = false,
    loginRequired = false
}) => {
    const location = useLocation();
    // Ensure exact match by comparing the full URL including hash
    const isCurrentPath = (location.pathname + location.hash) === url || isActive;
    
    // Auto-expand the section when it's the current path
    useEffect(() => {
        if (isCurrentPath && children && !isOpen && onClick) {
            onClick();
        }
    }, [isCurrentPath, children, isOpen, onClick]);
    
    // Only toggle dropdown when clicking the chevron, allow navigation otherwise
    const handleChevronClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onClick && onClick();
    };
    
    // Handle click on the item to both navigate and expand dropdown
    const handleItemClick = () => {
        if (children && !isOpen && onClick) {
            onClick();
        }
    };
    
    return (
        <div className={`${isSubItem ? 'ml-2' : ''}`}>
            <NavLink 
                to={url} 
                className={`group flex items-center justify-between w-full px-3 py-2 rounded-lg transition-all duration-200 ${isCurrentPath 
                    ? 'bg-gradient-to-r from-blue-50 to-transparent dark:from-slate-800 dark:to-transparent border-l-4 border-blue-500 dark:border-blue-400 font-medium' 
                    : 'hover:bg-blue-50 dark:hover:bg-slate-800 hover:border-blue-200 dark:hover:border-slate-700'}`}
                onClick={handleItemClick}
                end
            >
                <div className="flex items-center flex-grow">
                    <div className="flex items-center group-hover:translate-x-0.5 transition-transform duration-200">
                        {icon && (
                            <span className={`mr-2 transition-colors duration-200 ${isCurrentPath ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400'}`}>
                                {icon}
                            </span>
                        )}
                        <span className="text-sm">{title}</span>
                        {badge && (
                            <span className="ml-2 px-1 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 transition-transform duration-200 group-hover:scale-105">
                                {badge}
                            </span>
                        )}
                        {loginRequired && (
                            <span className="ml-2 transition-transform duration-200 group-hover:rotate-12">
                                <LockClosedIcon className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                            </span>
                        )}
                    </div>
                </div>
                
                {/* Always show chevron for items with children */}
                {children && (
                    <span 
                        onClick={(e) => {
                            e.preventDefault();
                            if (onClick) onClick();
                        }}
                        className="p-0.5 rounded-md hover:bg-blue-100 dark:hover:bg-slate-700 cursor-pointer transition-all duration-200 hover:scale-110"
                        aria-label={isOpen ? "Collapse section" : "Expand section"}
                    >
                        {isOpen ? (
                            <ChevronDownIcon className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 " />
                        ) : (
                            <ChevronRightIcon className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 " />
                        )}
                    </span>
                )}
            </NavLink>
            {isOpen && children && (
    <div className="mt-1 ml-4 space-y-1 border-l border-gray-200 dark:border-gray-700 pl-2 relative">
        {isCurrentPath && <div className="absolute -left-0.5 top-0 bottom-0 w-0.5 bg-blue-400/30 dark:bg-blue-500/30"></div>}
        {children}
    </div>
)}
        </div>
    );
};

const SubNavItem: React.FC<SubNavItemProps & { currentSection?: string }> = ({ title, url, isActive = false, innerNav, currentSection }) => {
    const location = useLocation();
    const [isInnerNavOpen, setIsInnerNavOpen] = useState(false);
    
    // Check if this item's URL exactly matches the current path or matches the currentSection
    let urlSectionId = "";
    if (url.includes("#")) {
        urlSectionId = url.split("#")[1];
    }
    // Normalize for comparison
    const normalizedCurrentSection = (currentSection || '').trim().toLowerCase();
    const normalizedUrlSectionId = urlSectionId.trim().toLowerCase();
    const isSectionActive = normalizedCurrentSection && normalizedUrlSectionId === normalizedCurrentSection;

    const exactUrlMatch = (location.pathname + location.hash) === url || isActive || isSectionActive;
    
    // Also check if any of the inner navigation items are active
    let isAnyInnerItemActive = false;
    if (innerNav && innerNav.length > 0) {
        // Normalize hash for robust, case-insensitive comparison
        const currentHash = location.hash.toLowerCase().replace(/^#+/, '');
        isAnyInnerItemActive = innerNav.some(item => {
            const [itemPath, itemHashRaw] = item.url.split('#');
            const itemHash = itemHashRaw ? itemHashRaw.toLowerCase().replace(/^#+/, '') : '';
            return (
                (location.pathname + location.hash) === item.url ||
                (location.pathname === itemPath && itemHash && currentHash && currentHash === itemHash)
            );
        });
    }
    
    // Consider this item active if either its URL exactly matches OR any of its inner items are active
    const isCurrentPath = exactUrlMatch || isAnyInnerItemActive;
    
    // Auto-open inner navigation when the current path matches the item's URL
    // or when any of the inner navigation items match the current path
    useEffect(() => {
        // Check if this item's URL exactly matches the current path
        if (isCurrentPath && innerNav && innerNav.length > 0) {
            setIsInnerNavOpen(true);
            return;
        }
        
        // Check if any of the inner navigation items match the current path
        if (innerNav && innerNav.length > 0) {
            const currentFullPath = location.pathname + location.hash;
            const hasActiveChild = innerNav.some(item => item.url === currentFullPath);
            if (hasActiveChild) {
                setIsInnerNavOpen(true);
            }
        }
    }, [isCurrentPath, innerNav, location.pathname, location.hash]);
    
    // Toggle inner navigation without navigating
    const handleChevronClick = (e: React.MouseEvent) => {
        // Stop event propagation to prevent the parent click handler from firing
        e.stopPropagation();
        // Toggle the inner navigation open/closed state
        setIsInnerNavOpen(!isInnerNavOpen);
    };
    
    // Handle click on the item to both navigate and toggle dropdown
    const handleItemClick = () => {
        if (innerNav && innerNav.length > 0) {
            setIsInnerNavOpen(true);
        }
    };
    
    // Use navigate for manual navigation
    const navigate = useNavigate();
    
    // Handle click on the main item
    const handleMainItemClick = () => {
        // Navigate to the URL
        navigate(url);
        
        // If it has inner navigation, expand it
        if (innerNav && innerNav.length > 0) {
            setIsInnerNavOpen(true);
        }
    };
    
    // Determine if this exact URL is active (strict equality)
    // We already calculated this above, so use isCurrentPath which includes both exact matches and inner item matches
    
    return (
        <div className="relative">
            <div 
                className={`flex items-center justify-between w-full group rounded-md transition-all duration-200 cursor-pointer relative
                    ${isCurrentPath 
                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-slate-800 font-medium shadow-sm' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-800 hover:shadow-sm'}`}
            >
                {isCurrentPath && <span className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 dark:bg-blue-400 rounded-l"></span>}
                <div 
                    className="flex-grow py-1 px-2 text-sm flex items-center transform transition-transform duration-200 hover:translate-x-0.5"
                    onClick={handleMainItemClick}
                >
                    <span className={`w-1 h-1 rounded-full mr-2 transition-all duration-200 ${isCurrentPath ? 'bg-blue-500 dark:bg-blue-400 scale-125' : 'bg-gray-300 dark:bg-gray-600'}`}></span>
                    <span>{title}</span>
                </div>
                
                {/* Always show chevron for items with inner navigation */}
                {innerNav && innerNav.length > 0 && (
                    <div 
                        onClick={handleChevronClick}
                        className="p-1 rounded-md hover:bg-blue-100 dark:hover:bg-slate-700 cursor-pointer transition-all duration-200 hover:scale-110 mr-1"
                        aria-label={isInnerNavOpen ? "Collapse section" : "Expand section"}
                    >
                        {isInnerNavOpen ? (
                            <ChevronDownIcon className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 " />
                        ) : (
                            <ChevronRightIcon className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 " />
                        )}
                    </div>
                )}
            </div>
            
            {/* Inner navigation items */}
            {isInnerNavOpen && innerNav && innerNav.length > 0 && (
    <div className="ml-4 mt-1 space-y-1 border-l border-gray-200 dark:border-gray-700 pl-2 relative">
        {/* Visual breadcrumb indicator for nested navigation */}
        {isCurrentPath && <div className="absolute -left-0.5 top-0 bottom-0 w-0.5 bg-blue-400/30 dark:bg-blue-500/30"></div>}
        {innerNav.map((item, index) => {
            // Highlight if either the full url matches, or if the pathname matches and the hash matches the item's hash
            // Normalize hashes for robust comparison
            const itemHash = item.url.includes('#') ? item.url.split('#')[1].toLowerCase().replace(/^#+/, '') : '';
            const currentHash = location.hash.toLowerCase().replace(/^#+/, '');
            const isInnerItemActive = ((location.pathname + location.hash) === item.url) ||
            (location.pathname === item.url.split('#')[0] && itemHash && currentHash && currentHash === itemHash);
            return (
                <div 
                    key={index}
                    className={`block py-1 px-2 text-xs rounded hover:bg-blue-50 dark:hover:bg-slate-800 transition-all duration-200 cursor-pointer relative
                        ${isInnerItemActive 
                        ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50/50 dark:bg-slate-800/50 shadow-sm' 
                        : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:translate-x-0.5'}`}
                    onClick={() => navigate(item.url)}
                >
                    {isInnerItemActive && (
                        <span className="absolute -left-0.5 w-1 h-5 bg-blue-500 dark:bg-blue-400 rounded-r"></span>
                    )}
                    <span className={`w-1 h-1 rounded-full mr-2 transition-all duration-200 ${isInnerItemActive ? 'bg-blue-500 dark:bg-blue-400 scale-125' : 'bg-gray-300 dark:bg-gray-600'}`}></span>
                    <span className="transition-colors duration-200">{item.title}</span>
                </div>
            );
        })}
    </div>
)}
        </div>
    );
};

const NavSection: React.FC<{title: string, children: React.ReactNode}> = ({ title, children }) => {
    return (
        <div className="mb-6 relative">
            <div className="mb-2 px-3 relative">
                <h3 className="text-xs font-semibold text-sky-600 dark:text-sky-400 uppercase tracking-wider group flex items-center">
                    <span className="transition-transform duration-200 group-hover:translate-x-0.5">{title}</span>
                </h3>
            </div>
            <div className="space-y-1.5 relative">
                {children}
            </div>
        </div>
    );
};

const DocumentationNav: React.FC<DocumentationNavProps> = ({ page, currentSection }) => {
    const location = useLocation();
    const documentationPreUrl = LinksEnum.Documentation;
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
    
    // Initialize expanded sections based on current page
    useEffect(() => {
        const initialExpanded: Record<string, boolean> = {};
        
        // Set initial expanded state based on current page
        if (page === PageEnum.Framework || page === PageEnum.VisualFramework || page === PageEnum.FrameworkIntroduction) {
            initialExpanded['framework'] = true;
        }
        if (page === PageEnum.PromptTechniques) {
            initialExpanded['techniques'] = true;
        }
        if (page === PageEnum.AdversarialPrompting || page === PageEnum.PromptInjections || 
            page === PageEnum.PromptLeaking || page === PageEnum.PromptJailbreak) {
            initialExpanded['adversarial'] = true;
        }
        if (page === PageEnum.PromptArchitecture) {
            initialExpanded['architecture'] = true;
        }
        if (page === PageEnum.PromptEngineering) {
            initialExpanded['engineering'] = true;
        }
        if (page === PageEnum.LargeLanguageModels) {
            initialExpanded['llm'] = true;
        }
        if (page === PageEnum.Applications) {
            initialExpanded['applications'] = true;
        }
        
        setExpandedSections(initialExpanded);
    }, [page]);
    
    const toggleSection = (sectionKey: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };
    
    const isExpanded = (sectionKey: string) => {
        return expandedSections[sectionKey] || false;
    };
    
    const userIsLoggedIn = () => {
        return !(localStorage.getItem("username") === "" || localStorage.getItem("username") === undefined || localStorage.getItem("username") === null);
    };

    // Define navigation data structures for reuse
    const craftingPhaseSub = [
        { title: "C - Context", url: `${documentationPreUrl}/framework#craft-c` },
        { title: "R - Register", url: `${documentationPreUrl}/framework#craft-r` },
        { title: "A - Acting Role", url: `${documentationPreUrl}/framework#craft-a` },
        { title: "F - Format", url: `${documentationPreUrl}/framework#craft-f` },
        { title: "T - Task", url: `${documentationPreUrl}/framework#craft-t` },
    ];

    const validationPhaseSub = [
        { title: "I - Interactive", url: `${documentationPreUrl}/framework#validate-i` },
        { title: "N - Non-Disclosure", url: `${documentationPreUrl}/framework#validate-n` },
        { title: "G - Goal-Driven", url: `${documentationPreUrl}/framework#validate-g` },
    ];

    const enhancementPhaseSub = [
        { title: "AI - Adapt & Improve", url: `${documentationPreUrl}/framework#enhance-ai` },
    ];

    const craftingPhaseVisualSub = [
        { title: "C - Context", url: `${documentationPreUrl}/framework/visual#craft-c` },
        { title: "R - Register", url: `${documentationPreUrl}/framework/visual#craft-r` },
        { title: "A - Acting Role", url: `${documentationPreUrl}/framework/visual#craft-a` },
        { title: "F - Format", url: `${documentationPreUrl}/framework/visual#craft-f` },
        { title: "T - Task", url: `${documentationPreUrl}/framework/visual#craft-t` },
    ];

    const validationPhaseVisualSub = [
        { title: "I - Interactive", url: `${documentationPreUrl}/framework/visual#validate-i` },
        { title: "N - Non-Disclosure", url: `${documentationPreUrl}/framework/visual#validate-n` },
        { title: "G - Goal-Driven", url: `${documentationPreUrl}/framework/visual#validate-g` },
    ];

    const enhancementPhaseVisualSub = [
        { title: "AI - Adapt & Improve", url: `${documentationPreUrl}/framework/visual#enhance-ai` },
    ];

    const shotPromptingSub = [
    { title: "Zero Shot", url: `${documentationPreUrl}/prompt-techniques#zero-shot` },
    { title: "Single Shot", url: `${documentationPreUrl}/prompt-techniques#single-shot` },
    { title: "Few Shot", url: `${documentationPreUrl}/prompt-techniques#few-shot` },
    { title: "Why important", url: `${documentationPreUrl}/prompt-techniques#why-important` },
];

    const websiteInjectionSub = [
    { title: "Robot.txt", url: `${documentationPreUrl}/adversarial-prompting/prompt-injections#robot-txt` },
    { title: "The injection", url: `${documentationPreUrl}/adversarial-prompting/prompt-injections#the-injection` },
    { title: "Risk Assessment", url: `${documentationPreUrl}/adversarial-prompting/prompt-injections#assessment` },
];

    const imageInjectionSub = [
    { title: "Image injection", url: `${documentationPreUrl}/adversarial-prompting/prompt-injections#image-injection` },
    { title: "Document injection", url: `${documentationPreUrl}/adversarial-prompting/prompt-injections#document-injection` },
    { title: "Risk Assessment", url: `${documentationPreUrl}/adversarial-prompting/prompt-injections#image-assessment` },
];

    const customGPTInjectionSub = [
    { title: "Pro's", url: `${documentationPreUrl}/adversarial-prompting/prompt-leaking#custom-gpt-injection-pros` },
    { title: "The injection", url: `${documentationPreUrl}/adversarial-prompting/prompt-leaking#custom-gpt-injection-the-injection` },
    { title: "Risk Assessment", url: `${documentationPreUrl}/adversarial-prompting/prompt-leaking#custom-gpt-injection-risk-assessment` },
];

    const asciiInjection = [
    { title: "ArtPrompt Injection", url: `${documentationPreUrl}/adversarial-prompting/prompt-jailbreak#artPrompt-injection` },
    { title: "Example", url: `${documentationPreUrl}/adversarial-prompting/prompt-jailbreak#artPrompt-injection-example` },
    { title: "Risk Assessment", url: `${documentationPreUrl}/adversarial-prompting/prompt-jailbreak#artPrompt-injection-risk-assessment` },
    { title: "Research papers", url: `${documentationPreUrl}/adversarial-prompting/prompt-jailbreak#artPrompt-injection-research-papers` },
];

    const emojiInjection = [
    { title: "Example", url: `${documentationPreUrl}/adversarial-prompting/prompt-jailbreak#emoji-jailbreak-examples` },
    { title: "Research papers", url: `${documentationPreUrl}/adversarial-prompting/prompt-jailbreak#emoji-jailbreak-research-papers` },
];

    const codingWithAISub = [
    { title: "Code Assistant Landscape", url: `${documentationPreUrl}/applications/coding#code-assistant-landscape` },
    { title: "Vibe Coding", url: `${documentationPreUrl}/applications/coding#vibe-coding` },
    { title: "AmpCoding", url: `${documentationPreUrl}/applications/coding#ampcoding` },
    { title: "Flushing Code", url: `${documentationPreUrl}/applications/coding#flushing-code` },
    { title: "Rules Files", url: `${documentationPreUrl}/applications/coding#rules-files` },
];

    return (
        <div className="col-span-12 lg:col-span-3 hidden lg:block sticky top-8 z-20"
             style={{"maxHeight": "95vh", "overflow": "auto", "marginTop": "50px", "scrollBehavior": "smooth"}}>
            <div className="px-4 py-6 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-100 dark:border-slate-800 transition-all duration-300 hover:shadow-md">
                {/* Navigation content starts here */}
                
                <nav className="space-y-8">
                    {/* Introduction & LLMs */}
                    <NavSection title="Introduction">
                        <NavItem 
                            title="Introduction" 
                            url={`${documentationPreUrl}/large-language-models#llms`}
                            icon={<BookOpenIcon className="h-5 w-5" />}
                            isActive={page === PageEnum.LargeLanguageModels}
                            isOpen={isExpanded('introduction')}
                            onClick={() => toggleSection('introduction')}
                            >
                                {isExpanded('introduction') ? (
                                    <>
                                        <SubNavItem currentSection={currentSection} title="What is an LLM?" url={`${documentationPreUrl}/large-language-models#what-is-an-llm`} />
                                        <SubNavItem currentSection={currentSection} title="Prediction based" url={`${documentationPreUrl}/large-language-models#prediction-based`} />
                                        <SubNavItem currentSection={currentSection} title="Token based" url={`${documentationPreUrl}/large-language-models#token-based`} />
                                        <SubNavItem currentSection={currentSection} title="Context Window" url={`${documentationPreUrl}/large-language-models#context-window`} />
                                        <SubNavItem currentSection={currentSection} title="LLM Configurations" url={`${documentationPreUrl}/large-language-models#llm-configurations`} />
                                        <SubNavItem currentSection={currentSection} title="Diffusion Models" url={`${documentationPreUrl}/large-language-models#diffusion-models`} />
                                    </>
                                ) : <div className="hidden"></div>}
                        </NavItem>
                        <NavItem 
                            title="Prompt Engineering" 
                            url={`${documentationPreUrl}/prompt-engineering#prompt-engineering`}
                            icon={<AcademicCapIcon className="h-5 w-5" />}
                            isActive={page === PageEnum.PromptEngineering}
                            isOpen={isExpanded('engineering')}
                            onClick={() => toggleSection('engineering')}
                        >
                            {isExpanded('engineering') ? (
                                <>
                                    <SubNavItem currentSection={currentSection} title="Interactive PE (IPE)" url={`${documentationPreUrl}/prompt-engineering#interactive-prompt-engineering`} />
                                    <SubNavItem currentSection={currentSection} title="Interactive Visual PE (IVPE)" url={`${documentationPreUrl}/prompt-engineering#interactive-visual-prompt-engineering`} />
                                    <SubNavItem currentSection={currentSection} title="System PE (SPE)" url={`${documentationPreUrl}/prompt-engineering#system-prompt-engineering`} />
                                </>
                            ) : <div className="hidden"></div>}
                        </NavItem>
                    </NavSection>
                    
                    {/* Framework */}
                    <NavSection title="Framework">
                        <NavItem 
                            title="Framework Introduction" 
                            url={`${documentationPreUrl}/framework/introduction#introduction`}
                            icon={<LightBulbIcon className="h-5 w-5" />}
                            isActive={page === PageEnum.FrameworkIntroduction}
                        />
                        <NavItem 
                            title="IPE Framework" 
                            url={`${documentationPreUrl}/framework#documentation`}
                            icon={<DocumentTextIcon className="h-5 w-5" />}
                            isActive={page === PageEnum.Framework}
                            isOpen={isExpanded('framework-ipe')}
                            onClick={() => toggleSection('framework-ipe')}
                        >
                            {isExpanded('framework-ipe') ? (
                                <>
                                    <SubNavItem currentSection={currentSection} title="Crafting Phase" url={`${documentationPreUrl}/framework#craft`} innerNav={craftingPhaseSub} />
                                    <SubNavItem currentSection={currentSection} title="Validation Phase" url={`${documentationPreUrl}/framework#validate`} innerNav={validationPhaseSub} />
                                    <SubNavItem currentSection={currentSection} title="Enhancement Phase" url={`${documentationPreUrl}/framework#enhance`} innerNav={enhancementPhaseSub} />
                                    <SubNavItem currentSection={currentSection} title="Cheatsheet" url={`${documentationPreUrl}/framework/cheatsheet#cheatsheet`} />
                                </>
                            ) : <div className="hidden"></div>}
                        </NavItem>
                        <NavItem 
                            title="IVPE Framework" 
                            url={`${documentationPreUrl}/framework/visual#documentation`}
                            icon={<DocumentTextIcon className="h-5 w-5" />}
                            isActive={page === PageEnum.VisualFramework}
                            isOpen={isExpanded('visual-framework')}
                            onClick={() => toggleSection('visual-framework')}
                        >
                            {isExpanded('visual-framework') ? (
                                <>
                                    <SubNavItem currentSection={currentSection} title="Crafting Phase" url={`${documentationPreUrl}/framework/visual#craft`} innerNav={craftingPhaseVisualSub} />
                                    <SubNavItem currentSection={currentSection} title="Validation Phase" url={`${documentationPreUrl}/framework/visual#validate`} innerNav={validationPhaseVisualSub} />
                                    <SubNavItem currentSection={currentSection} title="Enhancement Phase" url={`${documentationPreUrl}/framework/visual#enhance`} innerNav={enhancementPhaseVisualSub} />
                                </>
                            ) : <div className="hidden"></div>}
                        </NavItem>
                    </NavSection>
                    
                    {/* Techniques */}
                    <NavSection title="Techniques & Advanced">
                        <NavItem 
                            title="Prompt Techniques" 
                            url={`${documentationPreUrl}/prompt-techniques#prompt-techniques`}
                            icon={<BeakerIcon className="h-5 w-5" />}
                            isActive={page === PageEnum.PromptTechniques}
                            isOpen={isExpanded('techniques')}
                            onClick={() => toggleSection('techniques')}
                        >
                            {/* Always render children, but only show them when expanded */}
                            {isExpanded('techniques') ? (
                                <>
                                    <SubNavItem currentSection={currentSection} title="Shot Prompting" url={`${documentationPreUrl}/prompt-techniques#zero-single-few-shot`} innerNav={shotPromptingSub} />
                                    <SubNavItem currentSection={currentSection} title="Chain of Thought Prompting" url={`${documentationPreUrl}/prompt-techniques#chain-of-thought-prompting`} />
                                    <SubNavItem currentSection={currentSection} title="Emotional Prompting" url={`${documentationPreUrl}/prompt-techniques#emotional-prompting`} />
                                </>
                            ) : <div className="hidden"></div>}
                        </NavItem>
                        <NavItem 
                            title="Adversarial Prompting" 
                            url={`${documentationPreUrl}/adversarial-prompting#adversarial-prompting`}
                            icon={<ShieldExclamationIcon className="h-5 w-5" />}
                            isActive={page === PageEnum.AdversarialPrompting || page === PageEnum.PromptInjections || page === PageEnum.PromptLeaking || page === PageEnum.PromptJailbreak}
                            isOpen={isExpanded('adversarial')}
                            onClick={() => toggleSection('adversarial')}
                        >
                            {isExpanded('adversarial') ? (
                                <>
                                    <SubNavItem currentSection={currentSection} title="Introduction" url={`${documentationPreUrl}/adversarial-prompting#adversarial-prompting`} />
                                    <SubNavItem currentSection={currentSection} title="Prompt Injections" url={`${documentationPreUrl}/adversarial-prompting#prompt-injections`} />
                                    <SubNavItem currentSection={currentSection} title="Prompt Leaking" url={`${documentationPreUrl}/adversarial-prompting#prompt-leaking`} />
                                    <SubNavItem currentSection={currentSection} title="Prompt Jailbreaking" url={`${documentationPreUrl}/adversarial-prompting#prompt-jailbreaking`} />
                                    <SubNavItem currentSection={currentSection} title="Risk and Impact Score" url={`${documentationPreUrl}/adversarial-prompting#risk-impact-score`} />
                                    <SubNavItem currentSection={currentSection} title="Adversarial Overview" url={`${documentationPreUrl}/adversarial-prompting#prompt-injection-overview`} />
                                </>
                            ) : <div className="hidden"></div>}
                        </NavItem>
                        
                        {/* Detailed Adversarial Prompting Pages - Only visible when adversarial section is expanded */}
                        {isExpanded('adversarial') && (
                            <NavItem 
                                title="Prompt Injections" 
                                url={`${documentationPreUrl}/adversarial-prompting/prompt-injections#prompt-injections`}
                                icon={<ShieldExclamationIcon className="h-5 w-5" />}
                                isActive={page === PageEnum.PromptInjections}
                                isOpen={isExpanded('prompt-injections')}
                                onClick={() => toggleSection('prompt-injections')}
                                isSubItem={true}
                            >
                                {isExpanded('prompt-injections') ? (
                                    <>
                                        <SubNavItem currentSection={currentSection} title="Memory Manipulation" url={`${documentationPreUrl}/adversarial-prompting/prompt-injections#memory-injection`} />
                                        <SubNavItem currentSection={currentSection} title="Website Injections" url={`${documentationPreUrl}/adversarial-prompting/prompt-injections#website-injection`} innerNav={websiteInjectionSub} />
                                        <SubNavItem currentSection={currentSection} title="Image/Document Injections" url={`${documentationPreUrl}/adversarial-prompting/prompt-injections#image-injection`} innerNav={imageInjectionSub} />
                                    </>
                                ) : <div className="hidden"></div>}
                            </NavItem>
                        )}
                        
                        {isExpanded('adversarial') && (
                            <NavItem 
                                title="Prompt Leaking" 
                                url={`${documentationPreUrl}/adversarial-prompting/prompt-leaking#prompt-leaking`}
                                icon={<ShieldExclamationIcon className="h-5 w-5" />}
                                isActive={page === PageEnum.PromptLeaking}
                                isOpen={isExpanded('prompt-leaking')}
                                onClick={() => toggleSection('prompt-leaking')}
                                isSubItem={true}
                            >
                                {isExpanded('prompt-leaking') ? (
                                    <>
                                        <SubNavItem currentSection={currentSection} title="Custom GPT Leaking" url={`${documentationPreUrl}/adversarial-prompting/prompt-leaking#custom-gpt-leaking`} innerNav={customGPTInjectionSub} />
                                    </>
                                ) : <div className="hidden"></div>}
                            </NavItem>
                        )}
                        
                        {isExpanded('adversarial') && (
                            <NavItem 
                                title="Prompt Jailbreaking" 
                                url={`${documentationPreUrl}/adversarial-prompting/prompt-jailbreak#prompt-jailbreak`}
                                icon={<ShieldExclamationIcon className="h-5 w-5" />}
                                isActive={page === PageEnum.PromptJailbreak}
                                isOpen={isExpanded('prompt-jailbreak')}
                                onClick={() => toggleSection('prompt-jailbreak')}
                                isSubItem={true}
                            >
                                {isExpanded('prompt-jailbreak') ? (
                                    <>
                                        <SubNavItem currentSection={currentSection} title="ASCII Art Jailbreak" url={`${documentationPreUrl}/adversarial-prompting/prompt-jailbreak#ASCII-Art-based-jailbreak`} innerNav={asciiInjection} />
                                        <SubNavItem currentSection={currentSection} title="Emoji Jailbreak" url={`${documentationPreUrl}/adversarial-prompting/prompt-jailbreak#emoji-jailbreak`} innerNav={emojiInjection} />
                                        <SubNavItem currentSection={currentSection} title="Many-shot Jailbreak" url={`${documentationPreUrl}/adversarial-prompting/prompt-jailbreak#many-shot-jailbreak`} />
                                    </>
                                ) : <div className="hidden"></div>}
                            </NavItem>
                        )}
                        <NavItem 
                            title="Prompt Architecture" 
                            url={`${documentationPreUrl}/prompt-architecture#prompt-architecture`}
                            icon={<PuzzlePieceIcon className="h-5 w-5" />}
                            isActive={page === PageEnum.PromptArchitecture}
                            isOpen={isExpanded('architecture')}
                            onClick={() => toggleSection('architecture')}
                        >
                            {isExpanded('architecture') ? (
                                <>
                                    <SubNavItem currentSection={currentSection} title="Prompt Libraries" url={`${documentationPreUrl}/prompt-architecture#prompt-libraries-overview`} />
                                    <SubNavItem currentSection={currentSection} title="Software Engineering Perspective" url={`${documentationPreUrl}/prompt-architecture#software-engineering-perspective`} />
                                    <SubNavItem currentSection={currentSection} title="Prompt Component Library" url={`${documentationPreUrl}/prompt-architecture#prompt-component-library`} />
                                    <SubNavItem currentSection={currentSection} title="Prompt Recipes" url={`${documentationPreUrl}/prompt-architecture#prompt-recipes`} />
                                    <SubNavItem currentSection={currentSection} title="Workflow Prompting" url={`${documentationPreUrl}/prompt-architecture#prompt-workflows`} />
                                </>
                            ) : <div className="hidden"></div>}
                        </NavItem>
                    </NavSection>
                    
                    {/* Applications */}
                    <NavSection title="Applications">
                        {userIsLoggedIn() ? (
                            <>
                                <NavItem 
                                    title="Applications" 
                                    url={`${documentationPreUrl}/applications#applications`}
                                    icon={<CodeBracketIcon className="h-5 w-5" />}
                                    isActive={page === PageEnum.Applications}
                                    isOpen={isExpanded('applications')}
                                    onClick={() => toggleSection('applications')}
                                >
                                    {isExpanded('applications') ? (
                                        <>
                                            <SubNavItem currentSection={currentSection} title="Coding with AI" url={`${documentationPreUrl}/applications/coding#coding`} innerNav={codingWithAISub} />
                                            <SubNavItem currentSection={currentSection} title="ChatGPT" url={`${documentationPreUrl}/applications/chatgpt#chatgpt`} />
                                        </>
                                    ) : <div className="hidden"></div>}
                                </NavItem>
                            </>
                        ) : (
                            <>
                                <NavItem 
                                    title="Applications" 
                                    url={`${documentationPreUrl}/applications#applications`}
                                    icon={<CodeBracketIcon className="h-5 w-5" />}
                                    loginRequired={true}
                                />
                            </>
                        )}
                    </NavSection>
                    <NavSection title="Other pages">
                    {userIsLoggedIn() ? (
                            <>
                                <NavItem 
                                    title="Tools" 
                                    url="/tools#tools"
                                    icon={<WrenchScrewdriverIcon className="h-5 w-5" />}
                                    isActive={page === PageEnum.Tools}
                                />
                            </>
                        ) : (
                            <>
                                <NavItem 
                                    title="Tools" 
                                    url="/tools#tools"
                                    icon={<WrenchScrewdriverIcon className="h-5 w-5" />}
                                    loginRequired={true}
                                />
                            </>
                        )}
                    </NavSection>
                </nav>
            </div>
        </div>
    );

}

export default DocumentationNav;